import L from "leaflet";
import type { ForteXApi } from "@fortex/api";
import { layerId } from "../catalogue/types";
import type { CatalogueEntry } from "../catalogue/types";
import { featureFromMapObject, featureFromVector } from "../features/adapters";
import { geometryDistancePx } from "../features/FeatureIndex";
import { entryColor, paletteIndexOf } from "../features/style";
import type { Feature, FeatureBatch, ViewQuery } from "../features/types";
import { createTileInfoProvider } from "./tiles";
import type { InfoHit, InfoProvider, LayerSource, SourceFactory } from "./types";

export const hitRadiusPx = 10;

export type Bbox = [number, number, number, number];

const maxLatitude = 85.05112878;

export function tileX(lon: number, zoom: number): number {
  return ((lon + 180) / 360) * Math.pow(2, zoom);
}

export function tileY(lat: number, zoom: number): number {
  const clamped = Math.max(-maxLatitude, Math.min(maxLatitude, lat));
  const radians = (clamped * Math.PI) / 180;

  return ((1 - Math.log(Math.tan(radians) + 1 / Math.cos(radians)) / Math.PI) / 2) * Math.pow(2, zoom);
}

export function tileToLon(x: number, zoom: number): number {
  return (x / Math.pow(2, zoom)) * 360 - 180;
}

export function tileToLat(y: number, zoom: number): number {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, zoom);

  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

export function tileAlignedBbox(bounds: ViewQuery["bounds"], zoom: number): Bbox {
  const z = Math.max(0, Math.round(zoom));
  const scale = Math.pow(2, z);
  const minX = Math.max(0, Math.floor(tileX(bounds.minLon, z)));
  const maxX = Math.min(scale, Math.ceil(tileX(bounds.maxLon, z)));
  const minY = Math.max(0, Math.floor(tileY(bounds.maxLat, z)));
  const maxY = Math.min(scale, Math.ceil(tileY(bounds.minLat, z)));

  return [tileToLon(minX, z), tileToLat(maxY, z), tileToLon(maxX, z), tileToLat(minY, z)];
}

export type VectorSource = LayerSource & { loaded(): Feature[]; color: string };

export function vectorLayerId(entry: CatalogueEntry): string {
  return layerId(entry.root, entry.key);
}

export function createVectorSource(api: ForteXApi, colorIndex: (entry: CatalogueEntry) => number = paletteIndexOf): SourceFactory {
  return (entry, context) => {
    const vector = entry.vector;

    if (vector === null) return null;

    const pane = context.paneFor(entry);
    const group = L.layerGroup([], { pane });
    const id = vectorLayerId(entry);
    const color = entryColor(entry, colorIndex(entry));
    let loaded: Feature[] = [];
    let bounds: L.LatLngBounds | null = null;
    let current = entry;

    const load = async (view: ViewQuery, signal: AbortSignal): Promise<FeatureBatch> => {
      const bbox = tileAlignedBbox(view.bounds, view.zoom);
      const collection = await api.vector(vector.node, bbox, view.zoom, signal);
      const features = (collection.features ?? []).map(a => featureFromVector(a, id, entry, color));

      loaded = features;

      const extent = L.latLngBounds([]);

      for (const feature of features) extent.extend(feature.anchor);

      bounds = extent.isValid() ? extent : null;

      return { features, truncated: collection.truncated, grouped: collection.grouped, total: collection.total, zoom: collection.zoom };
    };

    const search = async (term: string, take: number, signal?: AbortSignal): Promise<Feature[]> => {
      const trimmed = term.trim();

      if (entry.kind !== "geoobject" || trimmed.length === 0) return [];

      const typeIds = entry.source?.typeIdNames.length ? entry.source.typeIdNames : [entry.idName];
      const list = await api.mapObjects({ typeIds, bounds: null, zoomLevel: null, group: false, search: trimmed, take, publishable: context.authenticated() ? null : true }, signal);

      return list.entities.map(a => featureFromMapObject(a, id));
    };

    const tileInfo = createTileInfoProvider(api, entry, context);
    const info: InfoProvider = {
      hitTest: async latlng => {
        const hits: InfoHit[] = [];

        for (const feature of loaded) {
          if (feature.cluster) continue;
          if (geometryDistancePx(feature.geometry, latlng, context.map) > hitRadiusPx) continue;

          hits.push({ id: feature.id, layer: entry.idName, name: feature.label ?? feature.id, feature });
        }

        return hits;
      },
      feature: (hit, latlng, zoom, signal) => tileInfo.feature(hit, latlng, zoom, signal),
      save: (hit, data) => tileInfo.save!(hit, data)
    };

    const source: VectorSource = {
      entry,
      kind: entry.kind,
      layer: group,
      pane,
      color,
      bounds: () => bounds ?? entryBoundsOf(current),
      features: { clustering: "server", load, search },
      info,
      legend: () => current.legend,
      loaded: () => loaded,
      update: next => {
        current = next;
        source.entry = next;
      },
      dispose: () => {
        loaded = [];
        group.clearLayers();
        group.remove();
      }
    };

    return source;
  };
}

function entryBoundsOf(entry: CatalogueEntry): L.LatLngBounds | null {
  const extent = entry.bounds ?? entry.source?.bounds ?? null;

  if (extent === null) return null;

  const result = L.latLngBounds([extent.minLat, extent.minLon], [extent.maxLat, extent.maxLon]);

  return result.isValid() ? result : null;
}

export function isVectorSource(source: LayerSource | null | undefined): source is VectorSource {
  return Boolean(source && typeof (source as VectorSource).loaded === "function");
}
